import { z } from 'zod';
import { baseRow, dateOnly, id, isoDate, listInput, nonEmpty, optionalText, updateOf, url } from './common';

export const EMAIL_PROVIDERS = ['google', 'microsoft'] as const;
export const emailProvider = z.enum(EMAIL_PROVIDERS);
export type EmailProvider = z.infer<typeof emailProvider>;

export const INTEGRATION_PROVIDERS = ['google', 'microsoft', 'stripe', 'quickbooks', 'xero', 'zapier', 'anthropic', 'sage', 'aws_s3', 'azure_blob'] as const;
export const integrationProvider = z.enum(INTEGRATION_PROVIDERS);
export type IntegrationProvider = z.infer<typeof integrationProvider>;

export const CONNECTION_STATUSES = ['pending', 'active', 'error', 'revoked'] as const;
export const connectionStatus = z.enum(CONNECTION_STATUSES);
export type ConnectionStatus = z.infer<typeof connectionStatus>;

export const EMAIL_DIRECTIONS = ['inbound', 'outbound'] as const;
export const emailDirection = z.enum(EMAIL_DIRECTIONS);

/** Events emitted to outgoing webhooks and the internal event bus (Queues). */
export const WEBHOOK_EVENTS = [
  'contact.created',
  'contact.updated',
  'company.created',
  'deal.created',
  'deal.updated',
  'deal.stage_changed',
  'deal.won',
  'deal.lost',
  'project.created',
  'project.updated',
  'proposal.sent',
  'proposal.signed',
  'change_order.sent',
  'change_order.approved',
  'change_order.rejected',
  'invoice.sent',
  'invoice.paid',
  'payment.succeeded',
  'payment.failed',
  'daily_log.submitted',
  'rfi.created',
  'rfi.answered',
  'lien_waiver.signed',
  'document.uploaded',
] as const;
export const webhookEvent = z.enum(WEBHOOK_EVENTS);
export type WebhookEvent = z.infer<typeof webhookEvent>;

// ---------------------------------------------------------------------------
// Connections (OAuth tokens are encrypted at rest; never returned to clients)
// ---------------------------------------------------------------------------

export const integrationConnectionSchema = baseRow.extend({
  provider: integrationProvider,
  userId: id.nullable(),
  status: connectionStatus,
  externalAccountId: z.string().nullable(),
  displayName: z.string().nullable(),
  scopes: z.array(z.string()),
  config: z.record(z.string(), z.unknown()),
  lastSyncedAt: z.date().nullable(),
  lastError: z.string().nullable(),
});
export type IntegrationConnection = z.infer<typeof integrationConnectionSchema>;

export const startOAuthInput = z.object({
  provider: z.enum(['google', 'microsoft', 'quickbooks', 'xero']),
  purpose: z.enum(['email_calendar', 'accounting']),
  returnTo: z.string().max(2048).optional(),
});
export const updateConnectionInput = z.object({ id, config: z.record(z.string(), z.unknown()).optional() });

// ---------------------------------------------------------------------------
// Email + calendar sync
// ---------------------------------------------------------------------------

export const emailAccountSchema = baseRow.extend({
  userId: id,
  connectionId: id,
  provider: emailProvider,
  address: z.string(),
  syncEnabled: z.boolean(),
  calendarSyncEnabled: z.boolean(),
  /** Share synced emails with whole workspace (true) or keep private to owner (false). */
  shareWithTeam: z.boolean(),
  historyCursor: z.string().nullable(),
  calendarCursor: z.string().nullable(),
  lastSyncedAt: z.date().nullable(),
  status: connectionStatus,
});
export type EmailAccount = z.infer<typeof emailAccountSchema>;
export const updateEmailAccountInput = z.object({
  id,
  syncEnabled: z.boolean().optional(),
  calendarSyncEnabled: z.boolean().optional(),
  shareWithTeam: z.boolean().optional(),
});

export const emailAddressEntry = z.object({ email: z.string(), name: z.string().optional() });
export const emailMessageSchema = baseRow.extend({
  emailAccountId: id,
  providerMessageId: z.string(),
  threadId: z.string().nullable(),
  direction: emailDirection,
  from: emailAddressEntry,
  to: z.array(emailAddressEntry),
  cc: z.array(emailAddressEntry),
  subject: z.string().nullable(),
  snippet: z.string().nullable(),
  bodyR2Key: z.string().nullable(),
  sentAt: z.date(),
  hasAttachments: z.boolean(),
  contactIds: z.array(id),
  dealId: id.nullable(),
  projectId: id.nullable(),
});
export type EmailMessage = z.infer<typeof emailMessageSchema>;
export const listEmailMessagesInput = listInput.extend({
  contactId: id.optional(),
  dealId: id.optional(),
  projectId: id.optional(),
  threadId: z.string().max(256).optional(),
});
export const sendEmailInput = z.object({
  emailAccountId: id,
  to: z.array(z.email()).min(1).max(50),
  cc: z.array(z.email()).max(50).default([]),
  bcc: z.array(z.email()).max(50).default([]),
  subject: nonEmpty(500),
  html: z.string().max(1_000_000),
  replyToMessageId: id.optional().nullable(),
  contactId: id.optional().nullable(),
  dealId: id.optional().nullable(),
  projectId: id.optional().nullable(),
  templateId: id.optional().nullable(),
});
export type SendEmailInput = z.infer<typeof sendEmailInput>;

export const calendarEventSchema = baseRow.extend({
  emailAccountId: id.nullable(),
  providerEventId: z.string().nullable(),
  title: z.string(),
  description: z.string().nullable(),
  location: z.string().nullable(),
  startsAt: z.date(),
  endsAt: z.date(),
  allDay: z.boolean(),
  attendees: z.array(z.object({ email: z.string(), name: z.string().optional(), response: z.string().optional() })),
  organizerUserId: id.nullable(),
  contactIds: z.array(id),
  dealId: id.nullable(),
  projectId: id.nullable(),
});
export type CalendarEvent = z.infer<typeof calendarEventSchema>;
export const createCalendarEventInput = z.object({
  emailAccountId: id.optional().nullable(),
  title: nonEmpty(300),
  description: optionalText(20_000),
  location: z.string().trim().max(500).optional().nullable(),
  startsAt: isoDate,
  endsAt: isoDate,
  allDay: z.boolean().default(false),
  attendees: z.array(z.object({ email: z.email(), name: z.string().max(200).optional() })).max(100).default([]),
  contactIds: z.array(id).max(50).default([]),
  dealId: id.optional().nullable(),
  projectId: id.optional().nullable(),
});
export type CreateCalendarEventInput = z.infer<typeof createCalendarEventInput>;
export const updateCalendarEventInput = updateOf(createCalendarEventInput);
export const listCalendarEventsInput = z.object({
  from: isoDate,
  to: isoDate,
  userId: id.optional(),
  contactId: id.optional(),
  dealId: id.optional(),
  projectId: id.optional(),
});

// ---------------------------------------------------------------------------
// Outgoing webhooks + API keys (open platform)
// ---------------------------------------------------------------------------

export const createWebhookEndpointInput = z.object({
  url,
  description: optionalText(500),
  events: z.array(webhookEvent).min(1),
  active: z.boolean().default(true),
});
export type CreateWebhookEndpointInput = z.infer<typeof createWebhookEndpointInput>;
export const updateWebhookEndpointInput = updateOf(createWebhookEndpointInput);
export const webhookEndpointSchema = baseRow.extend({
  url: z.string(),
  description: z.string().nullable(),
  events: z.array(webhookEvent),
  active: z.boolean(),
  /** Only the last 4 characters; full secret is shown once at creation. */
  secretLast4: z.string(),
  failureCount: z.number().int(),
  lastDeliveryAt: z.date().nullable(),
});
export type WebhookEndpoint = z.infer<typeof webhookEndpointSchema>;

export const WEBHOOK_DELIVERY_STATUSES = ['pending', 'succeeded', 'failed', 'dead'] as const;
export const webhookDeliveryStatus = z.enum(WEBHOOK_DELIVERY_STATUSES);
export const webhookDeliverySchema = baseRow.extend({
  endpointId: id,
  event: webhookEvent,
  payload: z.record(z.string(), z.unknown()),
  status: webhookDeliveryStatus,
  attempts: z.number().int(),
  responseStatus: z.number().int().nullable(),
  responseBody: z.string().nullable(),
  nextAttemptAt: z.date().nullable(),
  deliveredAt: z.date().nullable(),
});
export type WebhookDelivery = z.infer<typeof webhookDeliverySchema>;
export const listWebhookDeliveriesInput = listInput.extend({ endpointId: id.optional(), status: webhookDeliveryStatus.optional() });

export const API_KEY_SCOPES = ['read', 'write', 'admin'] as const;
export const apiKeyScope = z.enum(API_KEY_SCOPES);
export type ApiKeyScope = z.infer<typeof apiKeyScope>;
export const createApiKeyInput = z.object({
  name: nonEmpty(120),
  scopes: z.array(apiKeyScope).min(1).default(['read']),
  expiresAt: isoDate.optional().nullable(),
});
export type CreateApiKeyInput = z.infer<typeof createApiKeyInput>;
export const apiKeySchema = baseRow.extend({
  name: z.string(),
  prefix: z.string(),
  scopes: z.array(apiKeyScope),
  lastUsedAt: z.date().nullable(),
  expiresAt: z.date().nullable(),
  revokedAt: z.date().nullable(),
});
export type ApiKey = z.infer<typeof apiKeySchema>;

// ---------------------------------------------------------------------------
// AI
// ---------------------------------------------------------------------------

export const AI_TASKS = ['summarize_thread', 'draft_reply', 'tag_photo', 'tag_document', 'extract_estimate', 'daily_log_summary', 'ask'] as const;
export const aiTask = z.enum(AI_TASKS);
export type AiTask = z.infer<typeof aiTask>;
export const aiRequestInput = z.object({
  task: aiTask,
  entityType: z.string().max(64).optional(),
  entityId: id.optional(),
  prompt: z.string().max(20_000).optional(),
});
export type AiRequestInput = z.infer<typeof aiRequestInput>;

// ---------------------------------------------------------------------------
// Connections + mailbox management (integrations module)
// ---------------------------------------------------------------------------

export const startEmailOAuthInput = z.object({
  provider: emailProvider,
  /** Path inside the web app to return to after connecting (must start with a single `/`). */
  returnTo: z
    .string()
    .max(512)
    .regex(/^\/(?!\/)/, 'Must be an app path')
    .optional(),
});
export type StartEmailOAuthInput = z.infer<typeof startEmailOAuthInput>;

export const listMessagesForInput = listEmailMessagesInput.extend({
  emailAccountId: id.optional(),
  direction: emailDirection.optional(),
});

export const linkEmailMessageInput = z.object({
  id,
  dealId: id.nullable().optional(),
  projectId: id.nullable().optional(),
  contactIds: z.array(id).max(50).optional(),
});

export const reprocessDocumentInput = z.object({ documentId: id });
export const reprocessDrawingRevisionInput = z.object({ drawingRevisionId: id });

// ---------------------------------------------------------------------------
// Webhook endpoint management
// ---------------------------------------------------------------------------

export const webhookEndpointIdInput = z.object({ id });
export const redeliverWebhookInput = z.object({ deliveryId: id });
export const testWebhookInput = z.object({ id, event: webhookEvent.default('contact.created') });

// ---------------------------------------------------------------------------
// AI drafts. Every AI output is stored as a draft for human approval (never applied directly).
// ---------------------------------------------------------------------------

export const AI_DRAFT_KINDS = ['daily_log', 'punch_item', 'proposal', 'email', 'submittal_register', 'note'] as const;
export const aiDraftKind = z.enum(AI_DRAFT_KINDS);
export type AiDraftKind = z.infer<typeof aiDraftKind>;

export const AI_DRAFT_STATUSES = ['pending', 'approved', 'discarded'] as const;
export const aiDraftStatus = z.enum(AI_DRAFT_STATUSES);
export type AiDraftStatus = z.infer<typeof aiDraftStatus>;

/** Where a drafted value came from. `verified` = the quote was found verbatim in the source. */
export const aiCitation = z.object({
  field: z.string().max(120),
  quote: z.string().max(2000),
  sourceId: z.string().max(120),
  sourceLabel: z.string().max(200),
  start: z.number().int().nullable(),
  end: z.number().int().nullable(),
  page: z.number().int().nullable(),
  verified: z.boolean(),
});
export type AiCitation = z.infer<typeof aiCitation>;

const shortText = (max: number) => z.string().trim().max(max);

export const aiDailyLogContent = z.object({
  logDate: dateOnly,
  weather: z
    .object({ conditions: shortText(80).optional(), highF: z.number().optional(), lowF: z.number().optional() })
    .nullable()
    .default(null),
  workPerformed: shortText(50_000).nullable().default(null),
  manpower: z
    .array(z.object({ trade: shortText(80).min(1), workers: z.number().int().min(0).max(10_000), hours: z.number().min(0).max(100_000) }))
    .max(200)
    .default([]),
  delays: shortText(10_000).nullable().default(null),
  safetyNotes: shortText(10_000).nullable().default(null),
  visitors: shortText(5000).nullable().default(null),
  deliveries: shortText(5000).nullable().default(null),
});
export type AiDailyLogContent = z.infer<typeof aiDailyLogContent>;

export const aiPunchContent = z.object({
  title: shortText(300).min(1),
  description: shortText(10_000).nullable().default(null),
  trade: shortText(80).nullable().default(null),
  location: shortText(200).nullable().default(null),
  priority: z.enum(['low', 'normal', 'high', 'urgent']).default('normal'),
});
export type AiPunchContent = z.infer<typeof aiPunchContent>;

export const aiProposalContent = z.object({
  title: shortText(200).min(1),
  /** Plain text with blank-line paragraphs; converted to HTML on approval. */
  body: shortText(100_000).min(1),
});
export type AiProposalContent = z.infer<typeof aiProposalContent>;

export const aiEmailContent = z.object({
  to: z.array(z.email()).max(50).default([]),
  subject: shortText(500).min(1),
  body: shortText(100_000).min(1),
});
export type AiEmailContent = z.infer<typeof aiEmailContent>;

export const AI_SUBMITTAL_TYPES = ['shop_drawing', 'product_data', 'sample', 'mockup', 'certificate', 'warranty', 'other'] as const;
export const aiSubmittalRegisterContent = z.object({
  items: z
    .array(
      z.object({
        specSection: shortText(40).min(1),
        title: shortText(300).min(1),
        type: z.enum(AI_SUBMITTAL_TYPES).default('product_data'),
        description: shortText(5000).nullable().default(null),
        include: z.boolean().default(true),
      }),
    )
    .max(500),
});
export type AiSubmittalRegisterContent = z.infer<typeof aiSubmittalRegisterContent>;

export const aiNoteContent = z.object({ subject: shortText(300).min(1), body: shortText(20_000).min(1) });
export type AiNoteContent = z.infer<typeof aiNoteContent>;

export const aiDraftContent = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('daily_log'), data: aiDailyLogContent }),
  z.object({ kind: z.literal('punch_item'), data: aiPunchContent }),
  z.object({ kind: z.literal('proposal'), data: aiProposalContent }),
  z.object({ kind: z.literal('email'), data: aiEmailContent }),
  z.object({ kind: z.literal('submittal_register'), data: aiSubmittalRegisterContent }),
  z.object({ kind: z.literal('note'), data: aiNoteContent }),
]);
export type AiDraftContent = z.infer<typeof aiDraftContent>;

export const aiDraftSource = z.object({ type: z.string().max(64), id: z.string().max(120).nullable(), label: z.string().max(300) });
export type AiDraftSource = z.infer<typeof aiDraftSource>;

export const aiDraftSchema = z.object({
  id,
  kind: aiDraftKind,
  status: aiDraftStatus,
  title: z.string(),
  projectId: id.nullable(),
  dealId: id.nullable(),
  contactId: id.nullable(),
  /** What the draft was generated from (photo, document, deal, email thread, transcript). */
  source: aiDraftSource,
  content: aiDraftContent,
  citations: z.array(aiCitation),
  model: z.string(),
  warnings: z.array(z.string()),
  createdAt: z.date(),
  createdBy: id.nullable(),
  resolvedAt: z.date().nullable(),
  resolvedBy: id.nullable(),
  result: z.object({ entityType: z.string(), entityIds: z.array(id) }).nullable(),
});
export type AiDraft = z.infer<typeof aiDraftSchema>;

export const listAiDraftsInput = z.object({
  status: aiDraftStatus.optional(),
  kind: aiDraftKind.optional(),
  projectId: id.optional(),
  dealId: id.optional(),
  limit: z.number().int().min(1).max(200).default(50),
});

export const voiceDailyLogInput = z.object({
  projectId: id,
  logDate: dateOnly,
  /** Speech-to-text transcript (dictated on device or in the browser). */
  transcript: z.string().trim().min(10, 'Transcript is too short').max(60_000),
});
export const photoToPunchInput = z.object({
  photoId: id,
  /** Extra context typed by the user ("2nd floor hallway"). */
  hint: z.string().trim().max(500).optional(),
});
export const draftProposalInput = z.object({ dealId: id, instructions: z.string().trim().max(4000).optional() });
export const EMAIL_DRAFT_PURPOSES = ['follow_up', 'reply', 'introduction', 'schedule_visit', 'proposal_cover', 'project_update', 'custom'] as const;
export const draftEmailInput = z
  .object({
    dealId: id.optional(),
    contactId: id.optional(),
    projectId: id.optional(),
    purpose: z.enum(EMAIL_DRAFT_PURPOSES).default('follow_up'),
    instructions: z.string().trim().max(4000).optional(),
    replyToMessageId: id.optional(),
  })
  .refine((v) => v.dealId || v.contactId || v.projectId || v.replyToMessageId, 'Pick a deal, contact, project or email to draft from');
export const specToSubmittalsInput = z
  .object({
    projectId: id,
    documentId: id.optional(),
    specText: z.string().max(400_000).optional(),
  })
  .refine((v) => v.documentId || (v.specText && v.specText.trim().length >= 50), 'Upload a spec document or paste spec text');
export const approveAiDraftInput = z.object({
  id,
  /** Human edits to the drafted content; must match the draft's kind. */
  content: aiDraftContent.optional(),
  /** Email drafts: mailbox to send from. */
  emailAccountId: id.optional(),
});
export const discardAiDraftInput = z.object({ id, reason: z.string().trim().max(500).optional() });
